import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { DecimalInput, isNumber } from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import RedX from '../Graphics/RedX'
import { round } from 'altrac-conversions'

const DEFAULT_PHYSICAL = {
  min: 0,
  max: 100,
  zero: 0,
  precision: 1,
}

const FourToTwentyScale = (props) => {
  const {
    device: {
      physical = DEFAULT_PHYSICAL,
    } = {},
    targetDevice,
    targetNumber,
    setValidity,
  } = props

  const {
    [`min${targetDevice}${targetNumber}`]: min = DEFAULT_PHYSICAL.min,
    [`max${targetDevice}${targetNumber}`]: max = DEFAULT_PHYSICAL.max,
    [`zero${targetDevice}${targetNumber}`]: zero = DEFAULT_PHYSICAL.zero,
    [`precision${targetDevice}${targetNumber}`]: precision = DEFAULT_PHYSICAL.precision,
  } = physical

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [minHolder, setMinHolder] = useState(min)
  const [maxHolder, setMaxHolder] = useState(max)
  const [zeroHolder, setZeroHolder] = useState(zero)
  const [precisionHolder, setPrecisionHolder] = useState(precision)

  const appDispatch = useCallback(() => {
    const physicals = {
      [`min${targetDevice}${targetNumber}`]: minHolder,
      [`max${targetDevice}${targetNumber}`]: maxHolder,
      [`zero${targetDevice}${targetNumber}`]: Number(zeroHolder),
      [`precision${targetDevice}${targetNumber}`]: Number(precisionHolder),
    }

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [minHolder, maxHolder, zeroHolder, precisionHolder, targetDevice, targetNumber])

  useEffect(() => {
    appDispatch()
  }, [appDispatch])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'minHolder':
        newValue = round(value, 3)
        setMinHolder(newValue)
        break
      case 'maxHolder':
        newValue = round(value, 3)
        setMaxHolder(newValue)
        break
      case 'zeroHolder':
        newValue = round(value, 3)
        setZeroHolder(newValue)
        break
      case 'precisionHolder':
        newValue = round(value, 0)
        setPrecisionHolder(newValue)
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity])

  const unitStyle = { opacity: 0.7, marginBottom: '10px' }
  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueInRange = (
    value,
    min = 0,
    max = Number.MAX_VALUE
  ) => isNumber(value) && ((value < min) || (value > max)) && redX

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.device.fourToTwentyScale.label'
            defaultMessage='Current Calibration'
            description='Current Calibration label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.min'
              defaultMessage='Value Min'
              description='Value Min label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(minHolder * 10) / 10}
                childChange={childChange}
                field='minHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.max'
              defaultMessage='Value Max'
              description='Value Max label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(maxHolder * 10) / 10}
                childChange={childChange}
                field='maxHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.zero'
              defaultMessage='Zero'
              description='Zero label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(zeroHolder * 10) / 10}
                childChange={childChange}
                field='zeroHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.precision'
              defaultMessage='Precision'
              description='Precision label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={round(precisionHolder, 0)}
                childChange={childChange}
                field='precisionHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

FourToTwentyScale.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(FourToTwentyScale)
